import { observer } from "mobx-react-lite"

import type { WithdrawStore } from "@/domains/lock-page/withdraw-store"
import { Button } from "@/components/button"
import { Lozenge } from "@/domains/common/components/lozenge"
import { TickIcon } from "@/domains/icons"

export const WithdrawStateButtonsMap = observer(
  ({
    store,
    onCancel,
    onReset,
    onRetryReset,
    onRetryWithdraw,
    onWithdraw,
  }: {
    store: WithdrawStore
    onCancel: () => void
    onReset: () => void
    onRetryReset: () => void
    onRetryWithdraw: () => void
    onWithdraw: () => void
  }) => {
    if (["idle", "checking-expired", "not-expired", "expired", "checking-reset-required"].includes(store.state)) {
      return (
        <div className="flex items-center justify-end gap-2">
          <WithdrawButton onClick={() => {}} disabled={true} />
        </div>
      )
    }

    switch (store.state) {
      case "reset-required":
        return (
          <div className="flex items-center justify-end">
            <ResetButton onClick={onReset} />
          </div>
        )

      case "resetting":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )

      case "reset-failed":
        return (
          <div className="flex items-center justify-end gap-2">
            <RetryButton onRetry={onRetryReset} />
          </div>
        )

      case "withdraw-allowed":
        return (
          <div className="flex items-center justify-end gap-2">
            <WithdrawButton onClick={onWithdraw} />
          </div>
        )

      case "withdrawing":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )

      case "withdraw-failed":
        return (
          <div className="flex items-center justify-end gap-2">
            <RetryButton onRetry={onRetryWithdraw} />
          </div>
        )

      case "withdrawn":
        return (
          <div className="flex items-center justify-end gap-2">
            <WithdrawnButton />
          </div>
        )

      default:
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )
    }
  },
)

const CancelSoftButton = observer(({ onCancel, className }: { onCancel: () => void; className?: string }) => {
  return (
    <Button onClick={onCancel} variant="ghost" size="sm" className={className}>
      Cancel
    </Button>
  )
})

const RetryButton = observer(({ onRetry, className }: { onRetry: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onRetry} size="sm" className={className}>
      Retry
    </Button>
  )
})

const ResetButton = observer(({ onClick, className }: { onClick: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onClick} size="sm" className={className}>
      Reset
    </Button>
  )
})

const WithdrawButton = observer(
  ({ onClick, className, disabled }: { onClick: () => void; className?: string; disabled?: boolean }) => {
    return (
      <Button variant="primary" onClick={onClick} size="sm" className={className} disabled={disabled}>
        Withdraw
      </Button>
    )
  },
)

const WithdrawnButton = observer(() => {
  return (
    <Lozenge color="success" size="md" contentLeft={<TickIcon size={16} />}>
      Withdrawn
    </Lozenge>
  )
})
